import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { monitoringAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  MapPin, 
  RefreshCw, 
  Globe,
  Wifi,
  WifiOff,
  Users,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const Locations = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchLocations();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesRes, locationsRes] = await Promise.all([
        employeeAPI.getAll(),
        monitoringAPI.getAllLocations()
      ]);
      setEmployees(employeesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Locations fetch error:', error);
      toast.error('Failed to load location data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setRefreshing(true);
      const response = await monitoringAPI.getAllLocations();
      setLocations(response.data);
    } catch (error) {
      console.error('Location refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getEmployeeInfo = (employeeId) => {
    return employees.find(e => e.employee_id === employeeId);
  };

  const onlineCount = locations.length;
  const vpnCount = locations.filter(l => l.vpn_detected).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Online Employees</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {onlineCount}
                </p>
              </div>
              <Users className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Countries</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {[...new Set(locations.map(l => l.country))].length}
                </p>
              </div>
              <Globe className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={vpnCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${vpnCount > 0 ? 'text-red-600' : 'text-slate-500'}`}>VPN Detected</p>
                <p className={`text-3xl font-bold ${vpnCount > 0 ? 'text-red-700' : 'text-slate-900'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {vpnCount}
                </p>
              </div>
              <AlertTriangle className={`w-10 h-10 ${vpnCount > 0 ? 'text-red-400' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map Placeholder & Employee List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Map Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Live Location Map</CardTitle>
                <CardDescription>Real-time employee locations</CardDescription>
              </div>
              <Button 
                variant="outline" 
                onClick={fetchLocations}
                disabled={refreshing}
                data-testid="refresh-locations-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-slate-100 rounded-lg flex items-center justify-center relative overflow-hidden">
              {/* World Map Background */}
              <div className="absolute inset-0 opacity-10">
                <svg viewBox="0 0 1000 500" className="w-full h-full">
                  <path fill="#4F46E5" d="M150,100 Q200,50 250,100 T350,100 Q400,150 350,200 T250,200 Q200,250 150,200 T100,100 Z"/>
                  <path fill="#4F46E5" d="M450,80 Q500,50 550,80 T650,80 Q700,130 650,180 T550,180 Q500,230 450,180 T400,80 Z"/>
                  <path fill="#4F46E5" d="M700,120 Q750,70 800,120 T900,120 Q950,170 900,220 T800,220 Q750,270 700,220 T650,120 Z"/>
                  <path fill="#4F46E5" d="M300,250 Q350,200 400,250 T500,250 Q550,300 500,350 T400,350 Q350,400 300,350 T250,250 Z"/>
                  <path fill="#4F46E5" d="M600,280 Q650,230 700,280 T800,280 Q850,330 800,380 T700,380 Q650,430 600,380 T550,280 Z"/>
                </svg>
              </div>
              
              {/* Location Markers */}
              {locations.map((location, index) => {
                const emp = getEmployeeInfo(location.employee_id);
                // Random positions for demo
                const top = 20 + (index * 15) % 60;
                const left = 15 + (index * 20) % 70;
                
                return (
                  <div 
                    key={index}
                    className="absolute"
                    style={{ top: `${top}%`, left: `${left}%` }}
                  >
                    <div className={`relative group cursor-pointer`}>
                      <div className={`w-4 h-4 rounded-full ${location.vpn_detected ? 'bg-red-500' : 'bg-indigo-500'} animate-pulse`} />
                      <div className={`absolute w-8 h-8 rounded-full ${location.vpn_detected ? 'bg-red-500' : 'bg-indigo-500'} opacity-30 -top-2 -left-2 animate-ping`} />
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 whitespace-nowrap">
                          <p className="font-semibold text-slate-900">
                            {emp?.first_name} {emp?.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{location.city}, {location.country}</p>
                          <p className="text-xs text-slate-400 font-mono">{location.ip_address}</p>
                          {location.vpn_detected && (
                            <Badge className="bg-red-100 text-red-700 mt-1">VPN</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {locations.length === 0 && (
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No active locations</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Active Sessions</CardTitle>
            <CardDescription>Employees currently online</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {locations.map((location, index) => {
                const emp = getEmployeeInfo(location.employee_id);
                return (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      location.vpn_detected ? 'bg-red-50 border border-red-200' : 'bg-slate-50'
                    }`}
                    data-testid={`location-item-${location.employee_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={emp?.avatar} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                            {emp?.first_name?.[0]}{emp?.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                          location.vpn_detected ? 'bg-red-500' : 'bg-emerald-500'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {emp?.first_name} {emp?.last_name}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {location.city}, {location.country}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {location.vpn_detected ? (
                        <Badge className="bg-red-100 text-red-700">
                          <WifiOff className="w-3 h-3 mr-1" />
                          VPN
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Wifi className="w-3 h-3 mr-1" />
                          Direct
                        </Badge>
                      )}
                      <p className="text-xs text-slate-400 font-mono mt-1">
                        {location.ip_address}
                      </p>
                    </div>
                  </div>
                );
              })}

              {locations.length === 0 && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No employees online</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Locations;
